
import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { PlusCircle, Trash } from "lucide-react";
import { GuideSubmission } from "@/types/submission";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const categoryOptions = [
  { value: "printer_setup", label: "Printer Setup" },
  { value: "calibration", label: "Calibration" },
  { value: "maintenance", label: "Maintenance" },
  { value: "upgrades", label: "Upgrades" }
];

const subcategoryOptions = [
  { value: "firmware", label: "Firmware" },
  { value: "hardware", label: "Hardware" },
  { value: "software", label: "Software" },
  { value: "troubleshooting", label: "Troubleshooting" }
];

const difficultyOptions = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "expert", label: "Expert" }
];

const filters = [
  { value: "popular", label: "Popular" },
  { value: "essential", label: "Essential" },
  { value: "quick", label: "Quick" },
  { value: "advanced", label: "Advanced" }
];

// Define the schema for individual steps
const stepSchema = z.object({
  title: z.string().min(2, { message: "Title is required" }),
  description: z.string().min(10, { message: "Description must be at least 10 characters" })
});

// Define the schema for the entire form
const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  description: z.string().min(10, { message: "Description must be at least 10 characters" }),
  category: z.string({ required_error: "Category is required" }),
  subcategory: z.string({ required_error: "Subcategory is required" }),
  difficulty: z.string({ required_error: "Difficulty is required" }),
  filters: z.array(z.string()).min(1, { message: "Select at least one filter" }),
  steps: z.array(stepSchema).min(1, { message: "At least one step is required" }),
  videoLink: z.string().url({ message: "Must be a valid URL" }).optional().or(z.literal("")),
  writtenGuide: z.string().url({ message: "Must be a valid URL" }).optional().or(z.literal("")),
  requires_root: z.boolean().default(false)
});

interface GuideSubmissionFormProps {
  onSubmit: (data: GuideSubmission) => void;
  isSubmitting: boolean;
}

const GuideSubmissionForm = ({ onSubmit, isSubmitting }: GuideSubmissionFormProps) => {
  const form = useForm<GuideSubmission>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      category: "",
      subcategory: "",
      difficulty: "",
      filters: [],
      steps: [{ title: "", description: "" }],
      videoLink: "",
      writtenGuide: "",
      requires_root: false
    }
  });

  // Use useFieldArray for managing dynamic steps array
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "steps"
  });

  const addStep = () => {
    append({ title: "", description: "" });
  };

  const removeStep = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Guide Title</FormLabel>
              <FormControl>
                <Input placeholder="Enter guide title" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Describe what this guide covers" 
                  className="min-h-[100px]" 
                  {...field} 
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {categoryOptions.map((category) => (
                      <SelectItem key={category.value} value={category.value}>
                        {category.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="subcategory"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Subcategory</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {subcategoryOptions.map((subcategory) => (
                      <SelectItem key={subcategory.value} value={subcategory.value}>
                        {subcategory.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="difficulty"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Difficulty</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select difficulty" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {difficultyOptions.map((difficulty) => (
                      <SelectItem key={difficulty.value} value={difficulty.value}>
                        {difficulty.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="filters"
          render={() => (
            <FormItem>
              <FormLabel>Tags</FormLabel>
              <div className="flex flex-wrap gap-4">
                {filters.map((filter) => (
                  <FormField
                    key={filter.value}
                    control={form.control}
                    name="filters"
                    render={({ field }) => {
                      return (
                        <FormItem
                          key={filter.value}
                          className="flex flex-row items-start space-x-3 space-y-0"
                        >
                          <FormControl>
                            <Checkbox
                              checked={field.value?.includes(filter.value)}
                              onCheckedChange={(checked) => {
                                return checked
                                  ? field.onChange([...field.value, filter.value])
                                  : field.onChange(
                                      field.value?.filter(
                                        (value) => value !== filter.value
                                      )
                                    )
                              }}
                            />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer">
                            {filter.label}
                          </FormLabel>
                        </FormItem>
                      )
                    }}
                  />
                ))}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Steps</h3>
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={addStep}
              className="flex items-center gap-1"
            >
              <PlusCircle size={16} /> Add Step
            </Button>
          </div>
          
          {form.watch('steps').map((step, index) => (
            <Card key={index} className="relative">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex justify-between items-center">
                  <div>Step {index + 1}</div>
                  {form.watch('steps').length > 1 && (
                    <Button 
                      type="button"
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeStep(index)}
                      className="h-8 w-8 p-0"
                    >
                      <Trash size={16} className="text-destructive" />
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name={`steps.${index}.title`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Step Title</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Prepare the printer" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name={`steps.${index}.description`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Step Description</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Detailed instructions for this step"
                          className="min-h-[80px]" 
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="videoLink"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Video Link</FormLabel>
                <FormControl>
                  <Input placeholder="(Optional) YouTube or video URL" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="writtenGuide"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Additional Written Guide</FormLabel>
                <FormControl>
                  <Input placeholder="(Optional) URL to additional instructions" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="requires_root"
          render={({ field }) => (
            <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="font-normal cursor-pointer">
                This guide requires root access to the printer
              </FormLabel>
            </FormItem>
          )}
        />

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : "Submit Guide for Review"}
        </Button>
      </form>
    </Form>
  );
};

export default GuideSubmissionForm;
